import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DashboardWidget } from '../../services/dashboard';
import SortableWidget from '../SortableWidget';

interface SectionColumnProps {
  sectionId: string;
  columnIndex: number;
  widgets: DashboardWidget[];
  editMode: boolean;
  householdId: string;
  onUpdateWidget?: (widgetId: string, updates: Partial<DashboardWidget>) => void;
  onDeleteWidget?: (widgetId: string) => void;
  onConfigureWidget?: (widgetId: string) => void;
  onMoveToSection?: (widgetId: string, sectionId: string | null, columnIndex?: number) => void;
  onExpandWidget?: (widgetId: string) => void; // Expand widget to fullscreen
  availableSections?: Array<{ id: string; label: string }>;
  allWidgets?: DashboardWidget[]; // For nested sections
}

const SectionColumn: React.FC<SectionColumnProps> = ({
  sectionId,
  columnIndex,
  widgets,
  editMode,
  householdId,
  onUpdateWidget,
  onDeleteWidget,
  onConfigureWidget,
  onMoveToSection,
  onExpandWidget,
  availableSections = [],
  allWidgets = [],
}) => {
  // Make this column a droppable zone
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${sectionId}-${columnIndex}`,
    disabled: !editMode,
    data: {
      type: 'column',
      sectionId,
      columnIndex,
    },
  });

  const widgetIds = widgets.map(w => w.id);

  return (
    <div
      ref={setNodeRef}
      className={`section-column ${isOver ? 'drag-over' : ''} ${editMode ? 'edit-mode' : ''}`}
    >
      <SortableContext items={widgetIds} strategy={verticalListSortingStrategy}>
        {widgets.map(widget => (
          <SortableWidget
            key={widget.id}
            widget={widget}
            householdId={householdId}
            editMode={editMode}
            onUpdateWidget={onUpdateWidget}
            onDeleteWidget={onDeleteWidget}
            onConfigureWidget={onConfigureWidget}
            onMoveToSection={onMoveToSection}
            onExpandWidget={onExpandWidget}
            availableSections={availableSections}
            allWidgets={allWidgets}
            isInSection={true}
          />
        ))}
      </SortableContext>

      {/* Drop zone - always visible in edit mode at the bottom of the column */}
      {editMode && (
        <div className={`column-drop-zone ${widgets.length === 0 ? 'empty' : ''}`}>
          <span className="drop-zone-text">
            {widgets.length === 0 ? 'Drop widgets here' : '+ Drop to add'}
          </span>
        </div>
      )}

      {/* Minimum height when empty in view mode */}
      {widgets.length === 0 && !editMode && (
        <div className="column-empty-view" />
      )}
    </div>
  );
};

export default SectionColumn;
